import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Form, Row, Col, Spinner, ListGroup } from 'react-bootstrap';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import MenuService from '../../services/menuService';
import RoleService from '../../services/roleInclusaoService';
import { RoleInclusaoDTO } from '../../shared/dto/roleInclusaoDTO';
import DualListBox from '../../shared/DualListBox';
import CustomAlert from '../../shared/customAlert';

// Interface para os itens das listas visuais
interface ListItem {
  id: number | string;
  nome: string;
}

interface RawMenuItem {
  id: number;
  menu: { 
    id: number | string;
    nome: string;          
    nomeSubmenu?: string; 
    nomeItem?: string;     
    iconMenu?: string;
    rota?: string;
  };
  tipoPerfil: string;      
}

function RoleInclusaoAlteracaoComponent() {
  const navigate = useNavigate();
  const { tipo } = useParams();
  const location = useLocation();
  const { id } = location.state || {}; 

  // Estados do Formulário
  const [descricao, setDescricao] = useState('');
  const [tipoAcesso, setTipoAcesso] = useState('EXTERNO'); 
  const [nivelHierarquia, setNivelHierarquia] = useState(5);
  
  // Estados de Controle/Loading
  const [loading, setLoading] = useState(true);
  const [loadingMenus, setLoadingMenus] = useState(false); 
  const [loadingPais, setLoadingPais] = useState(false); 
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Listas de Dados
  const [rawMenus, setRawMenus] = useState<RawMenuItem[]>([]);
  const [allParentRolesList, setAllParentRolesList] = useState<ListItem[]>([]); 
  
  // Seleções do Usuário
  const [selectedMainMenu, setSelectedMainMenu] = useState<string | null>(null);
  const [selectedSubMenus, setSelectedSubMenus] = useState<ListItem[]>([]);
  const [selectedPais, setSelectedPais] = useState<ListItem[]>([]); 

  // Serviços
  const menuService = useMemo(() => new MenuService(), []);
  const roleService = useMemo(() => new RoleService(), []);

  // ==================================================================================
  // Helpers
  // ==================================================================================
  
  const formatMenuName = (menuObj: any): string => {
      if (!menuObj) return '';
      
      const menuPrincipal = menuObj.nome?.trim();
      const subMenuName = menuObj.nomeSubmenu?.trim();
      const itemName = menuObj.nomeItem?.trim();
      
      let fullName = menuPrincipal || '';
      
      if (subMenuName) fullName += ` / ${subMenuName}`;
      if (itemName) fullName += ` / ${itemName}`;
      
      return fullName;
  };

  // ==================================================================================
  // Carregamento de Dados (Callbacks)
  // ==================================================================================

  const fetchMenusPorTipo = useCallback(async (tipoParaBuscar: string) => {
    setLoadingMenus(true);
    try {
      // Se a API precisa de underscore, convertemos aqui antes de chamar
      const response = await menuService.listarMenu(tipoParaBuscar);
    setRawMenus(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar menus:", error);
      setAlert({ message: "Erro ao carregar lista de menus.", type: 'error' });
    } finally {
      setLoadingMenus(false);
    }
  }, [menuService]);

  const fetchRolesRestritas = useCallback(async (tipoParaBuscar: string, idAtual?: any) => {
    setLoadingPais(true);
    try {
      // Se a API precisa de underscore, convertemos aqui
      const tipoParaApi = tipoParaBuscar.replace(/ /g, '_');
      const response = await roleService.listarRolesRestritas(tipoParaApi);
      
      if (response && Array.isArray(response.data)) {
        let listaFormatada = response.data.map((role: any) => ({
            id: role.id,
            nome: role.descricao
        }));

        // 3. Remove o próprio ID (se for edição) para não ser pai de si mesmo
        if (idAtual) {
            listaFormatada = listaFormatada.filter((p: ListItem) => String(p.id) !== String(idAtual));
        }

        setAllParentRolesList(listaFormatada);
      } else {
        setAllParentRolesList([]);
      }
    } catch (error) {
      console.error("Erro ao buscar perfis pais:", error);
      setAllParentRolesList([]); 
    } finally {
      setLoadingPais(false);
    }
  }, [roleService]);

  // ==================================================================================
  // Effect Inicial (Carga de Dados)
  // ==================================================================================
  useEffect(() => {
    const carregarDadosIniciais = async () => {
      try {
        setLoading(true);

        if (tipo === 'alteracao' && id) {
          const roleResponse = await roleService.buscarRolePorId(id);
          const roleData = roleResponse.data;

      

          setDescricao(roleData.descricao);
          setTipoAcesso(roleData.tipoAcesso);
          setNivelHierarquia(roleData.nivelHierarquia);

      
        await fetchRolesRestritas(roleData.tipoAcesso, id);
          await fetchMenusPorTipo(roleData.tipoAcesso);

          if (roleData.idRoleOrigem && roleData.nomeRoleOrigem) {
             setSelectedPais([{ id: roleData.idRoleOrigem, nome: roleData.nomeRoleOrigem }]);
          }

          if (roleData.permissoesConcedidas && Array.isArray(roleData.permissoesConcedidas)) {
             const subMenusSelecionados: ListItem[] = [];
             
             roleData.permissoesConcedidas.forEach((item: any) => {
                const menuObj = item.menu; 
                if (menuObj) {
                    const displayName = formatMenuName(menuObj);
                    if (displayName) {
                       subMenusSelecionados.push({ id: menuObj.id, nome: displayName });
                    }
                }
             });
             setSelectedSubMenus(subMenusSelecionados.sort((a, b) => a.nome.localeCompare(b.nome)));
          }

        } else {
          // Inclusão: carrega padrão EXTERNO
          setTipoAcesso('EXTERNO');
          await fetchRolesRestritas('EXTERNO');
          await fetchMenusPorTipo('EXTERNO');
        }

      } catch (error: any) {
        console.error("Erro ao carregar dados iniciais:", error);
        setAlert({ message: "Não foi possível carregar os dados.", type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    carregarDadosIniciais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tipo]); 

  // ==================================================================================
  // Processamento Visual (Dual List)
  // ==================================================================================

  const mainMenuGroups = useMemo(() => {
    const groups: { [key: string]: ListItem[] } = {};
    
    rawMenus.forEach((item) => {
      const menuObj = item.menu;
      
      if (menuObj && typeof menuObj.nome === 'string' && menuObj.nome.trim() !== '') {
        const mainMenu = menuObj.nome.trim();
        if (!groups[mainMenu]) { groups[mainMenu] = []; }

        const displayName = formatMenuName(menuObj);
        if (displayName) {
          groups[mainMenu].push({ id: menuObj.id, nome: displayName });
        }
      }
    });
    
    Object.values(groups).forEach(group => group.sort((a: ListItem, b: ListItem) => a.nome.localeCompare(b.nome)));
    return groups;
  }, [rawMenus]);

  const mainMenus = useMemo(() => Object.keys(mainMenuGroups).sort(), [mainMenuGroups]);

  const availableSubMenus = useMemo(() => {
    if (!selectedMainMenu) return [];
    const allSubMenusForCategory = mainMenuGroups[selectedMainMenu] || [];
    const selectedIds = new Set(selectedSubMenus.map(s => s.id));
    return allSubMenusForCategory.filter(subMenu => !selectedIds.has(subMenu.id));
  }, [selectedMainMenu, mainMenuGroups, selectedSubMenus]);

  const availablePais = useMemo(() => {
    const selectedIds = new Set(selectedPais.map(p => p.id));
    return allParentRolesList
        .filter(pai => !selectedIds.has(pai.id))
        .sort((a: ListItem, b: ListItem) => a.nome.localeCompare(b.nome));
  }, [allParentRolesList, selectedPais]);

  // ==================================================================================
  // Handlers
  // ==================================================================================

  const handleTipoAcessoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const novoTipo = e.target.value;
    setTipoAcesso(novoTipo);
    
    // Atualiza as listas baseadas no NOVO tipo selecionado
    fetchMenusPorTipo(novoTipo);
    fetchRolesRestritas(novoTipo, id);
    
    // Limpa a seleção de pai pois o tipo mudou
    setSelectedPais([]); 
  };

  const handleMoveMenuToSelected = (items: ListItem[]) => setSelectedSubMenus(prev => [...prev, ...items].sort((a, b) => a.nome.localeCompare(b.nome)));
  
  const handleMoveMenuToAvailable = (items: ListItem[]) => {
    const ids = new Set(items.map(i => i.id));
    setSelectedSubMenus(prev => prev.filter(i => !ids.has(i.id)));
  };
  
  const handleMoveAllMenusToSelected = () => setSelectedSubMenus(prev => [...prev, ...availableSubMenus].sort((a, b) => a.nome.localeCompare(b.nome)));
  
  const handleMoveAllMenusToAvailable = () => {
    setSelectedSubMenus([]);
  };

  const handleMovePaiToSelected = (items: ListItem[]) => {
    if (selectedPais.length > 0 || items.length > 1) {
        setAlert({ message: "Selecione apenas 1 perfil pai.", type: 'error' });
        return;
    }
    setSelectedPais(items);
  };
  const handleMovePaiToAvailable = (items: ListItem[]) => {
    const ids = new Set(items.map(i => i.id));
    setSelectedPais(prev => prev.filter(i => !ids.has(i.id)));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const menuIdsArray = selectedSubMenus.map(menu => Number(menu.id));
    event.preventDefault();
    setAlert(null);
    if (!descricao.trim()) {
      setAlert({ message: "Descrição obrigatória.", type: 'error' });
      return;
    }
    setSaving(true);
    const roleParaSalvar: any = {
      descricao,
      tipoAcesso, 
      nivelHierarquia,
      menuIds: selectedSubMenus.map(menu => Number(menu.id)),
      status: true,
      idRoleOrigem: selectedPais.length > 0 ? String(selectedPais[0].id) : null
    };

    if (tipo === 'alteracao' && id) {
      roleParaSalvar.id = id;
    }

    if (selectedPais.length > 0) {
        roleParaSalvar.idRoleOrigem = String(selectedPais[0].id);
    }

   try {
    
      const responseRole = await roleService.salvarRole(roleParaSalvar);

      setAlert({ message: 'Perfil Alterado com sucesso', type: 'success' });
    } catch (error: any) {
      console.error(error);
      setAlert({ message: 'Erro ao salvar perfil ou menus.', type: 'error' });
    } finally {
      setSaving(false);
    }
};

  return (
    <div className="container">
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => { setAlert(null); if (alert.type === 'success') navigate(-1); }} />}

      <Form onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between mt-4">
          <h5>Configurações &gt; Perfis &gt; {tipo === 'inclusao' ? 'Cadastro' : 'Alteração'}</h5>
          <Button variant="secondary" onClick={() => navigate(-1)}> Voltar </Button>
        </div>
        <hr className="my-4" />

        {loading ? <div className="text-center"><Spinner animation="border" /></div> : (
          <>
            <Row>
              <Form.Group as={Col} md="6" className="mb-3">
                <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group as={Col} md="6" className="mb-3">
                <Form.Label>Tipo de Acesso <span className="text-danger">*</span></Form.Label>
                
                <Form.Select
                  value={tipoAcesso}
                  onChange={handleTipoAcessoChange}
                  required
                  disabled={tipo === 'alteracao'}
                >
                  <option value="EXTERNO">EXTERNO</option>
                  <option value="INTERNO">INTERNO</option>
                  <option value="EXTERNO CONSULTA">EXTERNO CONSULTA</option>
                  <option value="INTERNO CONSULTA">INTERNO CONSULTA</option>
                </Form.Select>
              </Form.Group>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Label>Vínculo com Perfil Pai (Opcional - Máx: 1) {loadingPais && <Spinner animation="border" size="sm" />}</Form.Label>
                <DualListBox
                  availableItems={availablePais}
                  selectedItems={selectedPais}
                  onMoveToSelected={handleMovePaiToSelected}
                  onMoveToAvailable={handleMovePaiToAvailable}
                  onMoveAllToSelected={() => setAlert({ message: "Selecione apenas 1.", type: 'error' })}
                  onMoveAllToAvailable={() => setSelectedPais([])}
                  availableLabel="Perfis Disponíveis"
                  selectedLabel="Perfil Vinculado"
                />
              </Col>
            </Row>

            <hr className="my-4" />
            <h5 className="mb-3">Permissões de Acesso (Menus) {loadingMenus && <Spinner animation="border" size="sm" />}</h5>
            <Row>
              <Col md={3}>
                <h6>Menus Principais</h6>
                <ListGroup style={{ height: '250px', overflowY: 'auto' }}>
                  {mainMenus.map(menu => (
                    <ListGroup.Item
                      key={menu}
                      action
                      active={selectedMainMenu === menu}
                      onClick={() => setSelectedMainMenu(menu)}
                      type="button"
                    >
                      {menu}
                    </ListGroup.Item>  
                  ))}
                </ListGroup>
              </Col>

              <Col md={9}>
                <DualListBox
                  availableItems={availableSubMenus}
                  selectedItems={selectedSubMenus}
                  onMoveToSelected={handleMoveMenuToSelected}
                  onMoveAllToSelected={handleMoveAllMenusToSelected}
                  onMoveToAvailable={handleMoveMenuToAvailable}
                  onMoveAllToAvailable={handleMoveAllMenusToAvailable}
                  availableLabel="Submenus Disponíveis"
                  selectedLabel="Permissões Concedidas"
                />
              </Col>
            </Row>
          </>
        )}
        <hr className="my-4" />
        <div className="d-flex justify-content-end">
          <Button type="submit" id="App-button-cor-principal" disabled={loading || saving}>
            {saving ? <><Spinner as="span" animation="border" size="sm" /> Salvando...</> : 'Salvar'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default RoleInclusaoAlteracaoComponent;